import {
  ARENA_ART as ART,
  CAMERA_STYLE,
  TRANSITION_STYLE,
  PAD_RADIUS,
  PAD_PORTRAIT_SCALE,
  BLAST_TTL_TICKS,
  HIT_INVULNERABLE_TICKS,
  HIT_FEEDBACK_STYLE,
  TILE,
  WEAPONS,
  aimDirection,
  aimPoint,
  beamLength,
  beamSpec,
  traceBeam,
  launchDirection,
  muzzleDistance,
  opponentOf,
  pelletDirections,
  projectileSpec,
  traceProjectile,
  vec,
  type Player,
  type PlayerId,
  type Square,
  type State,
  type Vec2,
} from '@chessshooter/shared';
import { ArenaEvents, type ArenaEvent } from './arenaFeedback';
import { HitFeedback, TimeLossPopups, formatHitLoss } from './hitFeedback';
import { ChessEntryCue } from './chess/entryCue';
import { transitionText } from './chess/status';
import { drawArena, drawNpcBoxes, drawNpcs, drawWeapon, disc } from './arenaArt';
import { screenToWorld, worldToScreen, type Camera, type Viewport } from './camera';
import type { BoardInputView } from './chess/boardInput';
import { drawBoardDrawings, type BoardDrawing } from './chess/boardDrawing';
import { fileRankOf, squareAt, squareCenter, squareOf, squareOrigin, type BoardLayout } from './chess/boardLayout';
import { loadPieceSprites } from './chess/pieces';
import { pieceAt, type Position } from './chess/position';
import type { PadView, TouchView } from './inputModel';
import { AIM_LINE, AIM_REACH, AIM_START, BEAM_STYLE, BLAST_STYLE, BOARD_COLORS, BOARD_STYLE, COLORS, TIER_RING, SHOT_STYLE, TIME_LOSS_STYLE, WEAPON_STYLE } from './uiConstants';

export interface View {
  me: PlayerId;
  /** Authoritative arena feedback online; local simulation is authoritative itself. */
  confirmedHits?: State;
  camera: Camera;
  /** null until a finger or pen has been used; keeps the overlays off desktop screens */
  touch: TouchView | null;
  /** mouse position in CSS px, for the facing tick on desktop */
  cursor: Vec2 | null;
  aimLine: boolean;
  dpr: number;
  viewport: Viewport;
  topInset: number;
  /** the board replaces the arena while this is set (chess phase) */
  board: BoardScene | null;
}

/** The board as drawn: its place on screen, the position and what the player is doing on it. */
export interface BoardScene {
  layout: BoardLayout;
  position: Position;
  input: BoardInputView;
  drawings?: readonly BoardDrawing[];
}

/**
 * Canvas 2D renderer. In the arena, world space is set up via the transform, so all shapes are drawn in
 * world units; the board is drawn in CSS px.
 */
export function createRenderer(canvas: HTMLCanvasElement) {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas 2d context unavailable');
  // bound to a non-null const so the drawing closures below see the narrowed type
  const ctx: CanvasRenderingContext2D = context;
  const hits = new HitFeedback();
  const timeLossPopups = new TimeLossPopups();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const arenaEvents = new ArenaEvents();
  const chessEntryCue = new ChessEntryCue();
  let flashes: { event: Extract<ArenaEvent, { kind: 'shot' }>; tick: number }[] = [];
  let lastTick = -1;
  const hitAt: [number, number] = [-Infinity, -Infinity];
  const sprites = loadPieceSprites(import.meta.env.BASE_URL);

  function resize(vp: Viewport, dpr: number): void {
    const w = Math.round(vp.width * dpr);
    const h = Math.round(vp.height * dpr);
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
  }

  const backdrop = document.createElement('canvas');
  let backdropKey = '';
  function drawMap(state: State): void {
    const key = `${state.map.width}:${state.map.height}:${state.map.tiles}:${JSON.stringify(state.pillars)}`;
    if (key !== backdropKey) {
      backdrop.width = state.map.width * TILE * ART.textureScale;
      backdrop.height = state.map.height * TILE * ART.textureScale;
      const bg = backdrop.getContext('2d');
      if (!bg) return;
      bg.scale(ART.textureScale, ART.textureScale);
      drawArena(bg, state);
      backdropKey = key;
    }
    ctx.drawImage(backdrop, 0, 0, state.map.width * TILE, state.map.height * TILE);
  }

  function drawCrates(state: State): void {
    for (const c of state.crates) {
      const x = c.pos.x - c.radius;
      const y = c.pos.y - c.radius;
      const size = c.radius * 2;
      ctx.fillStyle = COLORS.shadow;
      ctx.fillRect(x + ART.shadowX, y + ART.shadowY, size, size);
      ctx.fillStyle = COLORS.crate;
      ctx.strokeStyle = COLORS.crateEdge;
      ctx.lineWidth = ART.outline;
      ctx.beginPath();
      ctx.roundRect(x, y, size, size, ART.crateCorner);
      ctx.fill();
      ctx.stroke();
      ctx.strokeRect(x + ART.crateInset, y + ART.crateInset, size - ART.crateInset * 2, size - ART.crateInset * 2);
      ctx.beginPath();
      ctx.moveTo(x + ART.crateInset, y + ART.crateInset);
      ctx.lineTo(x + size - ART.crateInset, y + size - ART.crateInset);
      ctx.moveTo(x + size - ART.crateInset, y + ART.crateInset);
      ctx.lineTo(x + ART.crateInset, y + size - ART.crateInset);
      ctx.lineWidth = ART.crateBrace;
      ctx.strokeStyle = COLORS.crateLight;
      ctx.stroke();
      ctx.fillStyle = COLORS.crateLight;
      ctx.fillRect(x + ART.outline, y + ART.outline, size - ART.outline * 2, ART.outline);
    }
  }

  function drawPickups(state: State): void {
    const pulse = 1 + ART.pickupPulse * Math.sin(state.tick / ART.pickupPulseTicks);
    for (const p of state.pickups) {
      const tier = WEAPONS[p.weapon].tier;
      const radius = ART.pickupRadius * pulse;
      disc(ctx, p.pos.x, p.pos.y, radius + ART.pickupHalo, COLORS.shadow);
      disc(ctx, p.pos.x, p.pos.y, radius, COLORS.pickup);
      ctx.strokeStyle = TIER_RING[tier];
      ctx.lineWidth = ART.outline;
      ctx.stroke();
      ctx.save();
      ctx.translate(p.pos.x, p.pos.y);
      ctx.scale(ART.weaponScale, ART.weaponScale);
      drawWeapon(ctx, p.weapon);
      ctx.restore();
      ctx.font = ART.pickupLabelFont;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = COLORS.white;
      ctx.fillText(WEAPON_STYLE[p.weapon].name.toUpperCase(), p.pos.x, p.pos.y + ART.pickupLabelY);
    }
  }

  function drawPlayers(state: State, view: View): void {
    for (const p of state.players) {
      const age = state.tick - hitAt[p.id];
      if (age >= 0 && age < HIT_FEEDBACK_STYLE.ticks) {
        const progress = age / HIT_FEEDBACK_STYLE.ticks;
        ctx.save();
        ctx.globalAlpha = 1 - progress;
        ctx.strokeStyle = HIT_FEEDBACK_STYLE.color;
        ctx.lineWidth = HIT_FEEDBACK_STYLE.width;
        ctx.beginPath();
        ctx.arc(p.pos.x, p.pos.y, p.radius + HIT_FEEDBACK_STYLE.gap + progress * HIT_FEEDBACK_STYLE.growth, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      const own = p.id === view.me;
      const flashing = p.invulnerableTicks > 0 && Math.floor(p.invulnerableTicks / (HIT_INVULNERABLE_TICKS / 4 || 1)) % 2 === 0;
      const target = own ? previewTarget(p, view) : null;
      // The remote protocol carries movement, not mouse aim: never invent aim at a hidden opponent.
      const direction = target ? vec.sub(target, p.pos) : vec.len(p.vel) > 0 ? p.vel : { x: own ? 1 : -1, y: 0 };
      ctx.save();
      ctx.globalAlpha = flashing ? ART.projectileCore : 1;
      disc(ctx, p.pos.x + ART.shadowX, p.pos.y + ART.shadowY, p.radius, COLORS.shadow);
      if (own) {
        ctx.strokeStyle = COLORS.white;
        ctx.lineWidth = ART.playerRingWidth;
        ctx.beginPath();
        ctx.arc(p.pos.x, p.pos.y, p.radius + ART.playerRingGap, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.translate(p.pos.x, p.pos.y);
      ctx.rotate(Math.atan2(direction.y, direction.x));
      for (const sign of [-1, 1]) {
        disc(ctx, p.radius * ART.playerHandX, sign * p.radius * ART.playerHandY, p.radius * ART.playerHandRadius + ART.seam, COLORS.outline);
        disc(ctx, p.radius * ART.playerHandX, sign * p.radius * ART.playerHandY, p.radius * ART.playerHandRadius, own ? COLORS.meLight : COLORS.foeLight);
      }
      if (p.weapon) {
        ctx.save();
        ctx.translate(p.radius * ART.playerWeaponOffset, 0);
        ctx.scale(ART.weaponScale, ART.weaponScale);
        drawWeapon(ctx, p.weapon);
        ctx.restore();
      }
      disc(ctx, 0, 0, p.radius, COLORS.outline);
      disc(ctx, 0, 0, p.radius - ART.outline, own ? COLORS.me : COLORS.foe);
      disc(ctx, -ART.seam, -ART.seam, p.radius * ART.playerHighlight, own ? COLORS.meLight : COLORS.foeLight);
      ctx.fillStyle = COLORS.visor;
      ctx.beginPath();
      ctx.roundRect(p.radius * ART.playerVisorX, -p.radius * ART.playerVisorHeight / 2, p.radius * ART.playerVisorWidth, p.radius * ART.playerVisorHeight, ART.crateCorner);
      ctx.fill();
      ctx.fillStyle = COLORS.visorLight;
      ctx.fillRect(p.radius * ART.playerVisorX + ART.seam, -p.radius * ART.playerVisorHeight / 2 + ART.outline, ART.seam, p.radius * ART.playerVisorHeight / 2);
      ctx.restore();
      if (own) {
        ctx.font = ART.playerLabelFont;
        ctx.textAlign = 'center';
        ctx.fillStyle = COLORS.white;
        ctx.fillText('DU', p.pos.x, p.pos.y - ART.playerLabelY);
      }
    }
  }

  function drawProjectiles(state: State): void {
    ctx.lineCap = 'round';
    for (const p of state.projectiles) {
      const r = projectileSpec(p.weapon).projectileRadius;
      ctx.strokeStyle = WEAPON_STYLE[p.weapon].color;
      ctx.lineWidth = r * 2;
      ctx.globalAlpha = ART.trailAlpha;
      ctx.beginPath();
      ctx.moveTo(p.pos.x - p.vel.x * ART.trailTicks, p.pos.y - p.vel.y * ART.trailTicks);
      ctx.lineTo(p.pos.x, p.pos.y);
      ctx.stroke();
      ctx.globalAlpha = SHOT_STYLE.haloAlpha;
      disc(ctx, p.pos.x, p.pos.y, r * SHOT_STYLE.halo, WEAPON_STYLE[p.weapon].color);
      ctx.globalAlpha = 1;
      disc(ctx, p.pos.x, p.pos.y, r + ART.seam, COLORS.outline);
      disc(ctx, p.pos.x, p.pos.y, r, WEAPON_STYLE[p.weapon].color);
      disc(ctx, p.pos.x, p.pos.y, r * ART.projectileCore, COLORS.white);
    }
  }

  function drawBeams(state: State): void {
    ctx.lineCap = 'round';
    for (const b of state.beams) {
      const spec = beamSpec(b.weapon);
      const { length } = traceBeam(state, b.from, b.dir, beamLength(b), spec.width);
      const tip = { x: b.from.x + b.dir.x * length, y: b.from.y + b.dir.y * length };
      const life = b.ttlTicks / spec.ttlTicks;
      const flicker = spec.growTicks > 0 ? 1 + BEAM_STYLE.flickerFactor * Math.sin(state.tick * 1.7 + length) : 1;
      const width = spec.width * 2 * flicker;
      ctx.strokeStyle = WEAPON_STYLE[b.weapon].color;
      ctx.globalAlpha = BEAM_STYLE.glowAlpha * life;
      ctx.lineWidth = width * BEAM_STYLE.glowFactor;
      ctx.beginPath();
      ctx.moveTo(b.from.x, b.from.y);
      ctx.lineTo(tip.x, tip.y);
      ctx.stroke();
      ctx.globalAlpha = life;
      ctx.lineWidth = width;
      ctx.stroke();
      ctx.strokeStyle = COLORS.white;
      ctx.lineWidth = width * SHOT_STYLE.coreWidth;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  /** Explosions: a disc that grows to the blast radius while it fades. */
  function drawBlasts(state: State): void {
    for (const b of state.blasts) {
      const life = b.ttlTicks / BLAST_TTL_TICKS;
      const r = b.radius * (1 - (1 - BLAST_STYLE.startFraction) * life);
      ctx.fillStyle = WEAPON_STYLE[b.weapon].color;
      ctx.strokeStyle = WEAPON_STYLE[b.weapon].color;
      ctx.globalAlpha = BLAST_STYLE.fillAlpha * life;
      ctx.beginPath();
      ctx.arc(b.pos.x, b.pos.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = BLAST_STYLE.ringAlpha * life;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.strokeStyle = COLORS.white;
      ctx.lineWidth = ART.outline;
      for (let i = 0; i < SHOT_STYLE.sparks; i++) {
        const angle = i * Math.PI * 2 / SHOT_STYLE.sparks;
        const inner = r * (1 - life / 2), outer = inner + SHOT_STYLE.sparkLength * life;
        ctx.beginPath();
        ctx.moveTo(b.pos.x + Math.cos(angle) * inner, b.pos.y + Math.sin(angle) * inner);
        ctx.lineTo(b.pos.x + Math.cos(angle) * outer, b.pos.y + Math.sin(angle) * outer);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }

  function drawMuzzleFlashes(state: State): void {
    for (const { event, tick } of flashes) {
      const life = 1 - (state.tick - tick) / SHOT_STYLE.ticks;
      if (vec.len(event.dir) === 0) continue;
      ctx.save();
      ctx.translate(event.pos.x, event.pos.y);
      ctx.rotate(Math.atan2(event.dir.y, event.dir.x));
      ctx.translate(state.players[event.ownerId].radius + ART.weaponTip, 0);
      ctx.globalAlpha = life;
      ctx.fillStyle = WEAPON_STYLE[event.weapon].color;
      ctx.beginPath();
      ctx.moveTo(-ART.weaponTip, 0);
      ctx.lineTo(SHOT_STYLE.reach * life / 2, -SHOT_STYLE.spread * life);
      ctx.lineTo(SHOT_STYLE.reach * life, 0);
      ctx.lineTo(SHOT_STYLE.reach * life / 2, SHOT_STYLE.spread * life);
      ctx.closePath();
      ctx.fill();
      disc(ctx, ART.weaponTip, 0, SHOT_STYLE.spread * life / 2, COLORS.white);
      ctx.restore();
    }
  }

  function dot(p: Vec2, radius: number): void {
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Dots every AIM_LINE.spacing along a polyline, the first AIM_LINE.startGap in. A burst weapon
   * shows its rounds as groups: after every `burst` dots one is left out.
   */
  function dotPath(points: Vec2[], radius: number, burst: number): void {
    let travelled = 0;
    let next = AIM_LINE.startGap;
    let n = 0;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const segment = vec.dist(a, b);
      while (next <= travelled + segment) {
        const t = segment > 0 ? (next - travelled) / segment : 0;
        if (burst < 2 || n % (burst + 1) !== burst) dot({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, radius);
        n++;
        next += AIM_LINE.spacing;
      }
      travelled += segment;
    }
  }

  /** The point a shot would be aimed at right now: the cursor on desktop, far out along the pad or facing direction on touch. */
  function previewTarget(me: Player, view: View): Vec2 | null {
    const t = view.touch;
    if (t) return vec.add(me.pos, vec.scale(t.aiming ? t.aimDir : t.facing, AIM_REACH));
    if (view.cursor) return screenToWorld(view.camera, view.cursor);
    return null;
  }

  /**
   * How the held weapon would fire from here, in its colour: dots along the path of every pellet
   * (fan, ring, arc, bounce; a burst in groups), a band as wide as a beam up to its reach, a dashed
   * ring where a rocket would burst. Follows the assisted aim, so it shows where the shot really goes.
   */
  function drawShotPreview(state: State, view: View): void {
    if (!view.aimLine) return;
    const me = state.players[view.me];
    if (!me.weapon) return;
    const fireAt = previewTarget(me, view);
    if (!fireAt) return;
    const spec = WEAPONS[me.weapon];
    const dir = aimDirection(state, me, fireAt);
    const foe = state.players[opponentOf(view.me)];
    ctx.fillStyle = WEAPON_STYLE[me.weapon].color;
    ctx.strokeStyle = WEAPON_STYLE[me.weapon].color;
    if (spec.kind === 'beam') {
      let { length } = traceBeam(state, me.pos, dir, spec.range, spec.width);
      const hit = vec.rayCircle(me.pos, dir, foe.pos, foe.radius + spec.width);
      if (hit !== null && hit < length) length = hit;
      ctx.globalAlpha = AIM_LINE.beamAlpha;
      ctx.lineWidth = spec.width * 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(me.pos.x + dir.x * AIM_LINE.startGap, me.pos.y + dir.y * AIM_LINE.startGap);
      ctx.lineTo(me.pos.x + dir.x * length, me.pos.y + dir.y * length);
      ctx.stroke();
    } else {
      const launch = launchDirection(spec, dir, vec.dist(aimPoint(state, me, fireAt), me.pos), muzzleDistance(spec, me.radius));
      const radius = Math.max(AIM_LINE.dotRadius, spec.projectileRadius * AIM_LINE.dotScale);
      for (const d of pelletDirections(spec, launch)) {
        const path = traceProjectile(state, me, me.weapon, d);
        ctx.globalAlpha = AIM_LINE.alpha;
        dotPath([me.pos, ...path], radius, spec.burst);
        if (spec.blastRadius > 0) {
          const end = path[path.length - 1];
          ctx.globalAlpha = AIM_LINE.ringAlpha;
          ctx.lineWidth = 1.5;
          ctx.setLineDash(AIM_LINE.ringDash);
          ctx.beginPath();
          ctx.arc(end.x, end.y, spec.blastRadius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  function drawPad(pad: PadView, aim: boolean, aiming: boolean): void {
    ctx.fillStyle = pad.locked ? COLORS.padBaseLocked : COLORS.padBase;
    ctx.beginPath();
    ctx.arc(pad.origin.x, pad.origin.y, pad.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = COLORS.padRing;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    if (aim) {
      // the inner circle: releasing inside it cancels the shot
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.arc(pad.origin.x, pad.origin.y, pad.radius * AIM_START, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.fillStyle = aim && aiming ? COLORS.padKnobAiming : COLORS.padKnob;
    ctx.beginPath();
    ctx.arc(pad.knob.x, pad.knob.y, 20, 0, Math.PI * 2);
    ctx.fill();
  }

  function drawIdleSpot(spot: Vec2, radius: number): void {
    ctx.strokeStyle = COLORS.padRing;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(spot.x, spot.y, radius, 0, Math.PI * 2);
    ctx.stroke();
  }

  /** Screen-space overlays: the two pads, or the resting rings of locked pads while idle. */
  function drawPads(view: View): void {
    const t = view.touch;
    if (!t) return;
    ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
    const r = t.move?.radius ?? t.aim?.radius ?? PAD_RADIUS * (view.viewport.height > view.viewport.width ? PAD_PORTRAIT_SCALE : 1);
    if (t.move) drawPad(t.move, false, false);
    else if (t.spots.move) drawIdleSpot(t.spots.move, r);
    if (t.aim) drawPad(t.aim, true, t.aiming);
    else if (t.spots.aim) drawIdleSpot(t.spots.aim, r);
  }

  /** Only needed when zoomed: players, NPCs, boxes and the visible world rectangle remain legible. */
  function drawMinimap(state: State, view: View): void {
    const { camera, viewport, topInset } = view;
    if (camera.width <= viewport.width + ART.seam && camera.height <= viewport.height - topInset + ART.seam) return;
    const width = CAMERA_STYLE.minimapWidth;
    const scale = width / (state.map.width * TILE);
    const height = state.map.height * TILE * scale;
    const pad = CAMERA_STYLE.minimapPadding;
    const x = (viewport.width - width) / 2;
    const y = topInset + CAMERA_STYLE.minimapMargin;
    ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
    ctx.fillStyle = COLORS.outline;
    ctx.beginPath();
    ctx.roundRect(x - pad, y - pad, width + pad * 2, height + pad * 2, pad);
    ctx.fill();
    ctx.drawImage(backdrop, x, y, width, height);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.clip();
    ctx.strokeStyle = COLORS.white;
    ctx.lineWidth = ART.seam;
    ctx.strokeRect(x - camera.x / camera.scale * scale, y + (topInset - camera.y) / camera.scale * scale, viewport.width / camera.scale * scale, (viewport.height - topInset) / camera.scale * scale);
    for (const box of state.npcBoxes) {
      disc(ctx, x + box.pos.x * scale, y + box.pos.y * scale, ART.npcBox.minimapDot + ART.seam, COLORS.outline);
      disc(ctx, x + box.pos.x * scale, y + box.pos.y * scale, ART.npcBox.minimapDot, ART.tierColors[box.tier]);
    }
    for (const npc of state.npcs) {
      disc(ctx, x + npc.pos.x * scale, y + npc.pos.y * scale, ART.npc.minimapDot + ART.seam, COLORS.outline);
      disc(ctx, x + npc.pos.x * scale, y + npc.pos.y * scale, ART.npc.minimapDot, npc.ownerId === view.me ? COLORS.me : COLORS.foe);
    }
    for (const p of state.players) {
      disc(ctx, x + p.pos.x * scale, y + p.pos.y * scale, CAMERA_STYLE.minimapDot + ART.seam, COLORS.outline);
      disc(ctx, x + p.pos.x * scale, y + p.pos.y * scale, CAMERA_STYLE.minimapDot, p.id === view.me ? COLORS.me : COLORS.foe);
    }
    ctx.restore();
  }

  /**
   * The board: squares with coordinates, the last move, a glow under a king in check, the selected
   * piece with its targets (a dot on an empty square, a ring around a piece that can be taken), the
   * pieces, and while dragging a ghost on the origin and the piece under the finger.
   */
  function drawBoard(state: State, scene: BoardScene, view: View): void {
    const { layout, position, input } = scene;
    const { x, y, size, square } = layout;
    const S = BOARD_STYLE;
    ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
    ctx.fillStyle = BOARD_COLORS.frame;
    ctx.fillRect(x - S.frame, y - S.frame, size + 2 * S.frame, size + 2 * S.frame);
    const fillSquare = (sq: Square, color: string | CanvasGradient): void => {
      const o = squareOrigin(layout, sq);
      ctx.fillStyle = color;
      ctx.fillRect(o.x, o.y, square, square);
    };
    for (let file = 0; file < 8; file++) {
      for (let rank = 0; rank < 8; rank++) fillSquare(squareOf(file, rank), (file + rank) % 2 === 0 ? BOARD_COLORS.dark : BOARD_COLORS.light);
    }
    const last = state.chess.lastMove;
    if (last) {
      fillSquare(last.from, BOARD_COLORS.lastMove);
      fillSquare(last.to, BOARD_COLORS.lastMove);
    }
    if (input.premove) {
      fillSquare(input.premove.from, BOARD_COLORS.premove);
      fillSquare(input.premove.to, BOARD_COLORS.premove);
    }
    if (input.selected) fillSquare(input.selected, BOARD_COLORS.selected);
    if (position.check && position.king) {
      const c = squareCenter(layout, position.king);
      const glow = ctx.createRadialGradient(c.x, c.y, square * S.checkCore, c.x, c.y, square * S.checkGlow);
      glow.addColorStop(0, BOARD_COLORS.check);
      glow.addColorStop(1, BOARD_COLORS.checkFade);
      fillSquare(position.king, glow);
    }
    const hover = input.drag ? squareAt(layout, input.drag.at) : null;
    if (hover && input.targets.includes(hover)) {
      const o = squareOrigin(layout, hover);
      ctx.strokeStyle = BOARD_COLORS.hover;
      ctx.lineWidth = S.frame;
      ctx.strokeRect(o.x + S.frame / 2, o.y + S.frame / 2, square - S.frame, square - S.frame);
    }
    // coordinates: files along the bottom row, ranks up the left column, in the other square colour
    ctx.font = S.coordFont;
    for (let i = 0; i < 8; i++) {
      const bottom = squareAt(layout, { x: x + i * square + 1, y: y + size - 1 });
      const left = squareAt(layout, { x: x + 1, y: y + i * square + 1 });
      if (bottom) {
        const { file, rank } = fileRankOf(bottom);
        ctx.fillStyle = (file + rank) % 2 === 0 ? BOARD_COLORS.coordOnDark : BOARD_COLORS.coordOnLight;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(bottom[0], x + (i + 1) * square - S.coordInset, y + size - S.coordInset);
      }
      if (left) {
        const { file, rank } = fileRankOf(left);
        ctx.fillStyle = (file + rank) % 2 === 0 ? BOARD_COLORS.coordOnDark : BOARD_COLORS.coordOnLight;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(left[1], x + S.coordInset, y + i * square + S.coordInset);
      }
    }
    for (const row of position.board) {
      for (const cell of row) {
        if (!cell) continue;
        const o = squareOrigin(layout, cell.square);
        const lifted = input.drag !== null && input.drag.from === cell.square;
        if (lifted) ctx.globalAlpha = S.ghostAlpha;
        sprites.draw(ctx, cell.color, cell.type, o.x, o.y, square, view.dpr);
        if (lifted) ctx.globalAlpha = 1;
      }
    }
    ctx.fillStyle = BOARD_COLORS.target;
    ctx.strokeStyle = BOARD_COLORS.target;
    ctx.lineWidth = square * S.captureRingWidth;
    for (const t of input.targets) {
      const c = squareCenter(layout, t);
      ctx.beginPath();
      if (pieceAt(position, t)) {
        ctx.arc(c.x, c.y, square * S.captureRing, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.arc(c.x, c.y, square * S.targetDot, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    drawBoardDrawings(ctx, scene.drawings ?? [], layout);
    if (input.drag) {
      const piece = pieceAt(position, input.drag.from);
      if (piece) {
        const s = square * S.dragScale;
        sprites.draw(ctx, piece.color, piece.type, input.drag.at.x - s / 2, input.drag.at.y - s / 2, s, view.dpr);
      }
    }
  }

  function draw(state: State, view: View): void {
    const confirmed = view.confirmedHits ?? state;
    if (state.tick < lastTick) { arenaEvents.reset(confirmed); flashes = []; timeLossPopups.reset(); }
    lastTick = state.tick;
    const events = arenaEvents.update(confirmed);
    flashes = flashes.filter(f => state.winner === null && state.phase === 'shooter' && state.transitionTicks === 0 && state.tick - f.tick < SHOT_STYLE.ticks);
    for (const event of events) if (event.kind === 'shot') flashes.push({ event, tick: state.tick });
    const losses = hits.update(confirmed);
    const now = performance.now();
    const labels = timeLossPopups.update(losses, now);
    for (const id of [0, 1] as const) if (losses[id] > 0) hitAt[id] = state.tick;
    const { dpr, camera } = view;
    const transition = transitionText(state, view.me);
    const entryColor = chessEntryCue.update(state);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = view.board && !transition
      ? entryColor === 'w' ? BOARD_COLORS.backgroundWhite
        : entryColor === 'b' ? BOARD_COLORS.backgroundBlack : BOARD_COLORS.background
      : COLORS.background;
    ctx.fillRect(0, 0, view.viewport.width, view.viewport.height);
    if (transition) {
      ctx.fillStyle = TRANSITION_STYLE.color;
      ctx.textAlign = 'center';
      ctx.font = TRANSITION_STYLE.font;
      transition.forEach((line, i) => ctx.fillText(line, view.viewport.width / 2, view.topInset + (view.viewport.height - view.topInset) / 2 + (i - (transition.length - 1) / 2) * TRANSITION_STYLE.lineHeight));
      return;
    }
    if (view.board) {
      drawBoard(state, view.board, view);
      return;
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, view.topInset, view.viewport.width, Math.max(0, view.viewport.height - view.topInset));
    ctx.clip();
    ctx.setTransform(dpr * camera.scale, 0, 0, dpr * camera.scale, dpr * camera.x, dpr * camera.y);
    drawMap(state);
    drawPickups(state);
    drawCrates(state);
    drawNpcBoxes(ctx, state);
    drawNpcs(ctx, state, view.me);
    drawBeams(state);
    drawProjectiles(state);
    drawPlayers(state, view);
    drawMuzzleFlashes(state);
    drawBlasts(state);
    drawShotPreview(state, view);
    ctx.restore();
    drawMinimap(state, view);
    drawPads(view);
    // Screen-space labels follow the rendered figure and never shrink with overview zoom.
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const S = TIME_LOSS_STYLE;
    ctx.font = S.font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const player of state.players) {
      const label = labels[player.id];
      if (!label) continue;
      const pos = worldToScreen(camera, player.pos);
      if (pos.x < 0 || pos.x > view.viewport.width || pos.y < view.topInset || pos.y > view.viewport.height) continue;
      const progress = (now - label.startedAt) / S.durationMs;
      const text = formatHitLoss(label.ms);
      const width = ctx.measureText(text).width + S.padding * 2;
      const x = Math.max(S.margin + width / 2, Math.min(view.viewport.width - S.margin - width / 2, pos.x));
      const rise = reducedMotion.matches ? 0 : progress * S.rise;
      const y = Math.max(view.topInset + S.margin + S.height / 2, pos.y - player.radius * camera.scale - S.gap - S.height / 2 - rise);
      ctx.globalAlpha = Math.min(1, (1 - progress) / (1 - S.fadeStart));
      ctx.fillStyle = S.background;
      ctx.strokeStyle = S.border;
      ctx.lineWidth = S.edge;
      ctx.beginPath();
      ctx.roundRect(x - width / 2, y - S.height / 2, width, S.height, S.corner);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = S.text;
      ctx.fillText(text, x, y);
    }
    ctx.restore();
  }

  return { resize, draw };
}
